<template>
  <div class="pdf-outline-item">
    <div
      class="pdf-outline-item__title"
      :class="{ 'pdf-outline-item__title--active': item.pageNumber === currentPage }"
      :style="{ paddingLeft: `${16 + level * 16}px` }"
      @click="emit('select', item.pageNumber)"
    >
      <v-icon
        v-if="item.children && item.children.length > 0"
        :icon="expanded ? mdiChevronDown : mdiChevronRight"
        size="small"
        class="pdf-outline-item__toggle"
        @click.stop="expanded = !expanded"
      />
      <span class="pdf-outline-item__text">{{ item.title }}</span>
      <span class="pdf-outline-item__page">{{ item.pageNumber }}</span>
    </div>

    <Transition name="pdf-outline-collapse">
      <div v-if="expanded && item.children" class="pdf-outline-item__children">
        <PDFOutlineItem
          v-for="(child, index) in item.children"
          :key="index"
          :item="child"
          :current-page="currentPage"
          :level="level + 1"
          @select="emit('select', $event)"
        />
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, type PropType } from 'vue'
import { mdiChevronRight, mdiChevronDown } from '@mdi/js'
import type { PDFOutlineItem as OutlineItemType } from '../types'

defineProps({
  item: {
    type: Object as PropType<OutlineItemType>,
    required: true,
  },
  currentPage: {
    type: Number,
    required: true,
  },
  level: {
    type: Number,
    default: 0,
  },
})

const emit = defineEmits<{
  select: [page: number]
}>()

const expanded = ref(true)
</script>

<style lang="scss" scoped>
@use '@/styles/design-tokens' as *;

.pdf-outline-item {
  &__title {
    display: flex;
    align-items: center;
    gap: $space-xs;
    padding: $space-sm $space-lg;
    cursor: pointer;
    font-size: $text-base;
    @include transition-fast(background-color);
    @include focus-visible-only;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.05);
    }

    &--active {
      background: rgba(var(--v-theme-primary), 0.1);
      color: rgb(var(--v-theme-primary));
    }
  }

  &__toggle {
    flex-shrink: 0;
    margin-left: -4px;
  }

  &__text {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__page {
    flex-shrink: 0;
    font-size: $text-xs;
    color: rgba(var(--v-theme-on-surface), $opacity-tertiary);
    padding: 2px 6px;
    background: rgba(var(--v-theme-on-surface), 0.05);
    border-radius: $radius-sm;
  }

  &__children {
    // Children are indented via padding-left
  }
}

// Outline collapse transition
.pdf-outline-collapse-enter-active,
.pdf-outline-collapse-leave-active {
  transition: opacity $duration-normal $ease-default, max-height $duration-normal $ease-default;
  max-height: 2000px;
  overflow: hidden;
}

.pdf-outline-collapse-enter-from,
.pdf-outline-collapse-leave-to {
  opacity: 0;
  max-height: 0;
}

@include reduced-motion {
  .pdf-outline-item__title {
    transition: none;
  }

  .pdf-outline-collapse-enter-active,
  .pdf-outline-collapse-leave-active {
    transition: none;
  }
}
</style>
